import {
  type Column,
  type ColumnPinningState,
  type RowData,
} from '@tanstack/react-table';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type JSX,
  type Ref,
} from 'react';
import { useIntl } from 'react-intl';

import type { DataTableColumnSettingsModalRef } from './column-settings-types.js';
import {
  generateColumnOrderWithParentIds,
  generateColumnVisibilityObject,
  getCombinedVisibilityState,
  getHasColumnOrderChanged,
  getHasColumnPinningChanged,
  getHasVisibilityChanged,
  getMultipleLocalColumnVisibility,
  getVisibilityLimitMessage,
  getOverrideValue,
  getIsSettingEnabled,
  resolveEffectiveLimit,
} from './column-settings-utils.js';
import { DataTableAllColumnsHiddenEmptyState } from './DataTableAllColumnsHiddenEmptyState.js';
import { DataTableColumnsList } from './DataTableColumnsList.js';
import { DataTableVisibilityFilterControls } from './DataTableVisibilityFilterControls.js';
import { Flex } from '../../../../../layouts/flex/Flex.js';
import { useDataTable } from '../../../contexts/DataTableContext.js';
import type { DataTablePinnedColumnState } from '../../../features/ColumnPinning/column-pinning-types.js';
import type {
  DataTableColumnVisibilityState,
  DataTableVisibleColumnLimits,
} from '../../../features/ColumnVisibility/column-visibility-types.js';
import type { TDataTableFeatures } from '../../../features/types/features.types.js';
import { dataTableMessages } from '../../../messages.js';
import type { DataTableColumnSettingsConfig } from '../../../public.api.js';
import { DataTableSettingsModal } from '../../SettingsModal/DataTableSettingsModal.js';

interface DataTableColumnSettingsModalProps {
  /**
   * Whether column visibility settings are available in the modal.
   */
  visibilitySettings: boolean;
  /**
   * Whether column order settings are available in the modal.
   */
  columnOrderSettings: boolean;
  /**
   * Whether column pinning settings are available in the modal.
   */
  columnPinningSettings: boolean;
  /**
   * Default column visibility when settings are reset.
   */
  resetColumnVisibility?: DataTableColumnVisibilityState;
  /**
   * Default column order when settings are reset.
   */
  resetColumnOrder?: string[];
  /**
   * Default column pinning state when settings are reset.
   */
  resetColumnPinning?: DataTablePinnedColumnState;
  /**
   * Optional upper limits for the number of visible columns from the toolbar slot.
   */
  visibleColumnLimits?: DataTableVisibleColumnLimits;
}

/**
 * Modal combining UI controls for column visibility and column ordering.
 * All changes that made within the modal are only applied once the user click the Confirm button.
 * When clicking Close, the changes won't be applied and discarded.
 */
function ColumnSettingsModal<
  TFeatures extends TDataTableFeatures,
  TData extends RowData,
>(
  props: DataTableColumnSettingsModalProps & {
    onModalClose: () => void;
    columnSettingsOverride?: DataTableColumnSettingsConfig;
  },
): JSX.Element {
  const {
    visibilitySettings: toolbarVisibilitySettings,
    columnOrderSettings: toolbarColumnOrderSettings,
    columnPinningSettings: toolbarColumnPinningSettings,
    resetColumnOrder,
    resetColumnVisibility,
    resetColumnPinning,
    visibleColumnLimits: slotVisibleColumnLimits,
    onModalClose,
    columnSettingsOverride,
  } = props;

  const intl = useIntl();
  const table = useDataTable();
  const leafColumnsWithoutBuiltin = table.getLeafColumnsWithoutBuiltin();

  const visibilityOverride = columnSettingsOverride?.columnVisibility;
  const columnOrderOverride = columnSettingsOverride?.columnOrder;
  const columnPinningOverride = columnSettingsOverride?.columnPinning;

  // Programmatic override takes precedence over the slot value
  const rawVisibleColumnLimits =
    getOverrideValue(visibilityOverride, 'visibleColumnLimits') ??
    slotVisibleColumnLimits;

  const mandatoryVisibleColumnsCount = useMemo(
    () =>
      leafColumnsWithoutBuiltin.filter(
        (col) => col.columnDef.enableHiding === false,
      ).length,
    [leafColumnsWithoutBuiltin],
  );

  const { warningLimit, errorLimit } = useMemo(
    () =>
      resolveEffectiveLimit(
        rawVisibleColumnLimits,
        mandatoryVisibleColumnsCount,
      ),
    [rawVisibleColumnLimits, mandatoryVisibleColumnsCount],
  );

  const visibilitySettingsEnabled = getIsSettingEnabled(
    visibilityOverride,
    toolbarVisibilitySettings,
  );
  const columnOrderSettingsEnabled = getIsSettingEnabled(
    columnOrderOverride,
    toolbarColumnOrderSettings,
  );
  const columnPinningSettingsEnabled = getIsSettingEnabled(
    columnPinningOverride,
    toolbarColumnPinningSettings,
  );

  // original column order state
  const {
    columnOrderWithParents: tableColumnOrder,
    uniqueParentIds: allLocalColumnsParentIds,
  } = generateColumnOrderWithParentIds(
    table.getColumn,
    table.atoms.columnOrder?.get() ?? [],
  );

  // immediate search input value (drives the controlled input and clear button)
  const [filterInputValue, setFilterInputValue] = useState('');
  // debounced value that actually filters the column list
  const [activeFilterValue, setActiveFilterValue] = useState<string>('');

  const clearFilter = useCallback(() => {
    setFilterInputValue('');
    setActiveFilterValue('');
  }, []);

  // original column visibility state
  const initialColumnVisibility = useMemo(
    () => generateColumnVisibilityObject(leafColumnsWithoutBuiltin),
    [leafColumnsWithoutBuiltin],
  );

  // current column visibility state while modal is open
  const [localColumnVisibility, setLocalColumnVisibility] = useState<
    Record<string, boolean>
  >(initialColumnVisibility);

  // Merge columns that arrived after the modal opened (e.g. loading state) into the
  // draft so they reflect their actual visibility instead of defaulting to hidden.
  const newEntries = Object.entries(initialColumnVisibility).filter(
    ([id]) => !(id in localColumnVisibility),
  );
  if (newEntries.length > 0) {
    setLocalColumnVisibility((prev) => ({
      ...Object.fromEntries(newEntries),
      ...prev,
    }));
  }

  // current column order state while modal is open
  const [localColumnOrder, setLocalColumnOrder] =
    useState<string[]>(tableColumnOrder);

  // current column pinning state while modal is open
  const [localColumnPinning, setLocalColumnPinning] =
    useState<ColumnPinningState>(
      // original column pinning state
      () => table.store.state.columnPinning,
    );

  // flag whether the "Reset to default" status message for screen readers should be displayed
  const [showResetStatus, setShowResetStatus] = useState(false);
  // flag whether the lower-bound (min=1) error should be displayed
  const [showMinError, setShowMinError] = useState(false);

  const [listHiddenColumns, setListHiddenColumns] = useState(true);

  const onListHiddenColumnsChange = () => {
    setListHiddenColumns((prev) => !prev);
  };

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const contentNode = contentRef.current;
    if (!contentNode) {
      return;
    }

    const modalDialog = contentNode.closest<HTMLDivElement>('[role="dialog"]');
    if (!modalDialog) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect?.height;
      if (height && height > 0) {
        modalDialog.style.minHeight = CSS.supports('min-height', '1dvh')
          ? `min(${height}px, 96dvh)`
          : `min(${height}px, 96vh)`;
        observer.disconnect();
      }
    });
    observer.observe(modalDialog);
    return () => {
      observer.disconnect();
    };
  }, []);

  const columnOrderToReset =
    getOverrideValue(columnOrderOverride, 'resetColumnOrder') ??
    resetColumnOrder ??
    table.options.originalColumnOrder;
  const columnVisibilityToReset =
    getOverrideValue(visibilityOverride, 'resetColumnVisibility') ??
    resetColumnVisibility ??
    table.options.originalColumnVisibility;
  const columnPinningToReset =
    getOverrideValue(columnPinningOverride, 'resetColumnPinning') ??
    resetColumnPinning ??
    table.options.originalColumnPinning;

  // Memoize filtered column order for comparison
  const filteredLocalColumnOrder = useMemo(
    () =>
      localColumnOrder.filter(
        (colId) => !allLocalColumnsParentIds.includes(colId),
      ),
    [localColumnOrder, allLocalColumnsParentIds],
  );

  // checks if local default column visibility has changed
  const hasVisibilityChanged = useMemo(
    () =>
      visibilitySettingsEnabled &&
      getHasVisibilityChanged(columnVisibilityToReset, localColumnVisibility),
    [visibilitySettingsEnabled, columnVisibilityToReset, localColumnVisibility],
  );

  // checks if local default column order has changed
  const hasColumnOrderChanged =
    columnOrderSettingsEnabled &&
    getHasColumnOrderChanged(columnOrderToReset, filteredLocalColumnOrder);

  // checks if local default column pinning state has changed
  const hasColumnPinningChanged =
    columnPinningSettingsEnabled &&
    getHasColumnPinningChanged(
      {
        left: columnPinningToReset?.left ?? [],
        right: columnPinningToReset?.right ?? [],
      },
      localColumnPinning,
    );

  // Drives the "Show all"/"Hide all" button disabled states.
  const showHideAllState = getCombinedVisibilityState(
    localColumnVisibility,
    leafColumnsWithoutBuiltin,
    true,
  );

  const visibleColumnCount = Object.values(localColumnVisibility).filter(
    Boolean,
  ).length;
  const totalColumnCount = Object.values(localColumnVisibility).length;
  const hiddenColumnCount = totalColumnCount - visibleColumnCount;

  const exceedsError =
    errorLimit !== undefined && visibleColumnCount > errorLimit;

  const limitMessage = getVisibilityLimitMessage(
    showMinError,
    errorLimit,
    warningLimit,
    visibleColumnCount,
    intl,
  );

  /**
   * Updates the local column visibility state.
   * @param column - the column to update.
   * @param currentVisibilityState - the current visibility state of the column.
   */
  const updateLocalColumnVisibility = (
    column: Column<TFeatures, TData>,
    currentVisibilityState: boolean | 'indeterminate',
  ): void => {
    const childColumns = column.columns;
    if (childColumns.length === 0) {
      setLocalColumnVisibility((prevState) => ({
        ...prevState,
        [column.id]: !currentVisibilityState,
      }));
    } else {
      setLocalColumnVisibility((prevState) =>
        getMultipleLocalColumnVisibility(
          childColumns,
          currentVisibilityState,
          prevState,
        ),
      );
    }
    showResetStatus && setShowResetStatus(false);
    showMinError && setShowMinError(false);
  };

  /**
   * Updates the local column order state.
   * @param newColumnOrder - the new column order.
   */
  const updateLocalColumnOrder = (newColumnOrder: string[]): void => {
    setLocalColumnOrder(newColumnOrder);
    showResetStatus && setShowResetStatus(false);
  };

  /**
   * Updates the local column pinning state.
   * @param newState - the new column pinning state.
   */
  const updateLocalColumnPinning = (newState: ColumnPinningState): void => {
    setLocalColumnPinning(newState);
    showResetStatus && setShowResetStatus(false);
  };

  const clearStatusMessages = () => {
    showResetStatus && setShowResetStatus(false);
    showMinError && setShowMinError(false);
  };

  const onShowAll = () => {
    setLocalColumnVisibility((prevState) =>
      getMultipleLocalColumnVisibility(
        leafColumnsWithoutBuiltin,
        // passing false sets all hideable columns to visible
        false,
        prevState,
      ),
    );
    clearStatusMessages();
  };

  const onHideAll = () => {
    setLocalColumnVisibility((prevState) =>
      getMultipleLocalColumnVisibility(
        leafColumnsWithoutBuiltin,
        // passing true sets all hideable columns to hidden
        true,
        prevState,
      ),
    );
    clearStatusMessages();
  };

  const showFilterControl =
    visibilitySettingsEnabled || columnPinningSettingsEnabled;

  // The visibility control row (the "list hidden columns" switch + counter)
  // is hidden while a search is active.
  const showVisibilityControlRow =
    visibilitySettingsEnabled && !filterInputValue;

  // During search, always include hidden columns so they can be found by name.
  const effectiveListHiddenColumns =
    listHiddenColumns || activeFilterValue.length > 0;

  // Show the empty state when all columns are hidden and hidden columns are not listed
  const showAllColumnsHiddenEmptyState =
    visibilitySettingsEnabled &&
    visibleColumnCount === 0 &&
    !effectiveListHiddenColumns;

  const onReset = () => {
    let didReset = false;

    // reset visibility, order and/or pinning state if they are enabled
    if (visibilitySettingsEnabled && columnVisibilityToReset) {
      setLocalColumnVisibility(
        generateColumnVisibilityObject(
          leafColumnsWithoutBuiltin,
          columnVisibilityToReset,
        ),
      );
      didReset = true;
    }

    if (columnOrderSettingsEnabled && columnOrderToReset) {
      const { columnOrderWithParents } = generateColumnOrderWithParentIds(
        table.getColumn,
        columnOrderToReset,
      );

      setLocalColumnOrder(columnOrderWithParents);
      didReset = true;
    }

    if (columnPinningSettingsEnabled && columnPinningToReset) {
      setLocalColumnPinning({
        left: columnPinningToReset?.left ?? [],
        right: columnPinningToReset?.right ?? [],
      });
      didReset = true;
    }

    // if settings were successfully reset,
    // show a status message for the screen reader
    didReset && setShowResetStatus(true);
    showMinError && setShowMinError(false);
  };

  const onApply = () => {
    if (visibilitySettingsEnabled && visibleColumnCount < 1) {
      setShowMinError(true);
      return;
    }
    table.setColumnVisibility(localColumnVisibility);
    table.setColumnOrder(filteredLocalColumnOrder);
    table.setColumnPinning(localColumnPinning);
    onModalClose();
  };

  return (
    <DataTableSettingsModal
      title={intl.formatMessage(dataTableMessages.columnSettingsTitle)}
      show={true}
      showResetStatus={showResetStatus}
      onDismiss={onModalClose}
      onReset={onReset}
      onApply={onApply}
      disableButtons={{
        reset:
          !hasColumnOrderChanged &&
          !hasVisibilityChanged &&
          !hasColumnPinningChanged,
        apply: exceedsError,
      }}
      footerMessage={limitMessage}
      controlLabels={{
        reset: intl.formatMessage(
          dataTableMessages.resetColumnSettingsButtonText,
        ),
        cancel: intl.formatMessage(dataTableMessages.cancelButtonText),
        apply: intl.formatMessage(dataTableMessages.applyButtonText),
      }}
    >
      <Flex ref={contentRef} flexDirection="column" gap={12} flex={1}>
        <DataTableVisibilityFilterControls
          showVisibilityControl={visibilitySettingsEnabled}
          showFilterControl={showFilterControl}
          filterValue={filterInputValue}
          onFilterValueChange={setFilterInputValue}
          setActiveFilterValue={setActiveFilterValue}
          showAllDisabled={showHideAllState === true}
          hideAllDisabled={showHideAllState === false}
          onShowAll={onShowAll}
          onHideAll={onHideAll}
          listHiddenColumns={listHiddenColumns}
          onListHiddenColumnsChange={onListHiddenColumnsChange}
          hiddenColumnCount={hiddenColumnCount}
          visibleColumnCount={visibleColumnCount}
          totalColumnCount={totalColumnCount}
        />
        {showAllColumnsHiddenEmptyState ? (
          <Flex flex={1} alignItems="center" justifyContent="center">
            <DataTableAllColumnsHiddenEmptyState
              onListHiddenColumns={onListHiddenColumnsChange}
            />
          </Flex>
        ) : (
          <DataTableColumnsList
            visibilitySettings={visibilitySettingsEnabled}
            columnOrderSettings={columnOrderSettingsEnabled}
            columnPinningSettings={columnPinningSettingsEnabled}
            activeFilterValue={activeFilterValue}
            listHiddenColumns={effectiveListHiddenColumns}
            localColumnVisibility={localColumnVisibility}
            updateLocalColumnVisibility={updateLocalColumnVisibility}
            localColumnOrder={localColumnOrder}
            updateLocalColumnOrder={updateLocalColumnOrder}
            localColumnPinning={localColumnPinning}
            updateLocalColumnPinning={updateLocalColumnPinning}
            hasFilterBar={showFilterControl}
            hasVisibilityControl={showVisibilityControlRow}
            hasMessage={limitMessage !== undefined}
            onClearFilter={clearFilter}
          />
        )}
      </Flex>
    </DataTableSettingsModal>
  );
}

function DataTableColumnSettingsModalFwd(
  props: DataTableColumnSettingsModalProps,
  forwardedRef: Ref<DataTableColumnSettingsModalRef>,
): JSX.Element | null {
  const [modalOpen, setModalOpen] = useState(false);
  const [columnSettingsOverride, setColumnSettingsOverride] =
    useState<DataTableColumnSettingsConfig>();

  // open the modal from outside
  useImperativeHandle(forwardedRef, () => ({
    openColumnSettings: (columnSettings?: DataTableColumnSettingsConfig) => {
      setColumnSettingsOverride(columnSettings);
      setModalOpen(true);
    },
  }));

  if (modalOpen) {
    return (
      <ColumnSettingsModal
        {...props}
        columnSettingsOverride={columnSettingsOverride}
        onModalClose={() => setModalOpen(false)}
      />
    );
  }
  return null;
}

/**
 * @internal
 */
export const DataTableColumnSettingsModal = forwardRef(
  DataTableColumnSettingsModalFwd,
);
